import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import time from 'utils/time';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import { CrmContactImage } from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactItem/CrmContactItem';
import { validatePlaceholders } from 'components/Dashboard/Influence/CrmEmails/CrmEmailComponent/emailSignatureFunctions';
import InteractionTags from './InteractionTags';
import getInteractionDisplayType from './getInteractionDisplayType';

const InteractionCard = ({ item, onNavigate, compact }) => {
  const { id, dateTime, addedByUserFullName, description, crmContactIds, crmContactNames, crmContactImageIds, tags } =
    item;
  // Old email campaigns stored legacy mail-merge chips (e.g. "salutation_text") directly in the
  // description - normalize them to today's "@salutation" pill before rendering the preview.
  // preserveUnrecognizedFields: true - no team mailMergeFields list is available here, so a valid
  // custom-field pill (e.g. "@nick-name") must be left alone rather than flagged as unrecognized.
  const previewDescription = useMemo(
    () =>
      description ? validatePlaceholders({ emailBody: description, preserveUnrecognizedFields: true }) : description,
    [description]
  );
  const location = useLocation();
  const [hoveredStakeholderIndex, setHoveredStakeholderIndex] = useState(null);
  const hasStakeholders = (crmContactIds?.length ?? 0) > 0;
  const visibleStakeholders = (crmContactIds ?? []).slice(0, 8).map((_, index) => ({
    name: crmContactNames?.[index],
    imageId: crmContactImageIds?.[index],
  }));
  const remainingAvatarsCount = (crmContactIds?.length ?? 0) - visibleStakeholders.length;
  const remainingStakeholderNames = (crmContactNames ?? []).slice(visibleStakeholders.length);
  const displayType = getInteractionDisplayType(item);

  const avatarStack = hasStakeholders && (
    <div className='d-flex align-items-center flex-shrink-0' onClick={(e) => e.preventDefault()}>
      {visibleStakeholders.map((stakeholder, index) => (
        <div
          key={index}
          onMouseEnter={() => setHoveredStakeholderIndex(index)}
          onMouseLeave={() => setHoveredStakeholderIndex((current) => (current === index ? null : current))}
          data-tooltip-content={stakeholder.name}
          data-tooltip-id='general-tooltip'
          style={{
            lineHeight: 0,
            position: 'relative',
            borderRadius: '50%',
            boxShadow: '0 0 0 2px #fff',
            marginLeft: index === 0 ? 0 : -8,
            zIndex: hoveredStakeholderIndex === index ? visibleStakeholders.length + 1 : index,
          }}
        >
          <CrmContactImage imageId={stakeholder.imageId} size={25} />
        </div>
      ))}
      {remainingAvatarsCount > 0 && (
        <div
          className='d-flex align-items-center justify-content-center paragraph-p3 font-weight-bold bg-main-blue position-relative text-white specific-x-padding-6 specific-y-padding-0 avatar-overflow-badge'
          data-tooltip-content={remainingStakeholderNames.join('\n')}
          data-tooltip-id='scrollable-list-tooltip'
          style={{ zIndex: visibleStakeholders.length }}
        >{`+${thousandCommas(remainingAvatarsCount)}`}</div>
      )}
    </div>
  );

  return (
    <li className='my-2 interaction-list-item box-list-item'>
      <Link
        to={`/people/interactions/${id}`}
        // So "Back to interactions" can return to the exact list (including any active search)
        // instead of a bare /people/interactions - see InfluenceCrmSubNav.jsx's own back link.
        state={{ returnUrl: `${location.pathname}${location.search}` }}
        className='d-block px-4 py-3 text-decoration-none'
        onClick={onNavigate}
        target={compact ? '_blank' : undefined}
        rel={compact ? 'noreferrer' : undefined}
      >
        {compact ? (
          // Compact: always name + actions on one row (actions grouped so they stay clustered on
          // the right, not spread out by GeneralItemActions' unwrapped sibling icons), avatars below.
          <>
            <div className='d-flex justify-content-between align-items-center'>
              <p className='mb-0 paragraph-p1 text-light-blue-100'>{addedByUserFullName}</p>
              <div className='d-flex align-items-center'>
                <GeneralItemActions item={item} influenceItem={true} itemActionsProps={{ noDots: true }} />
              </div>
            </div>
            {hasStakeholders && <div className='d-flex specific-top-margin-10 pb-1'>{avatarStack}</div>}
          </>
        ) : (
          <>
            {/* Mobile: name + checkbox on one row, avatars on their own line below */}
            <div className='d-flex d-sm-none justify-content-between align-items-center'>
              <p className='mb-0 paragraph-p1 text-light-blue-100'>{addedByUserFullName}</p>
              <GeneralItemActions item={item} influenceItem={true} itemActionsProps={{ noDots: true }} />
            </div>
            {hasStakeholders && <div className='d-flex d-sm-none specific-top-margin-10'>{avatarStack}</div>}

            {/* Desktop: name left, avatars + actions together on the right */}
            <div className='d-none d-sm-flex justify-content-between align-items-center'>
              <p className='mb-0 paragraph-p1 text-light-blue-100'>{addedByUserFullName}</p>
              <div className='d-flex align-items-center'>
                {avatarStack}
                <div className='ml-2 d-flex align-items-center'>
                  <GeneralItemActions item={item} influenceItem={true} itemActionsProps={{ noDots: true }} />
                </div>
              </div>
            </div>
          </>
        )}

        <p className='mb-0 title-h4 paragraph-p1 main-light-text hidden-lines hidden-one-line d-flex align-items-center specific-top-margin-5'>
          <span>{displayType}</span>
          <span className='dot specific-right-margin-6'></span>
          <span>{time(dateTime).format('DD MMM[,] YYYY')}</span>
        </p>

        {description && (
          <div
            className='editor-content-preview border-left-2 border-main-blue pl-3 line-clamp-1 specific-top-margin-14 specific-bottom-margin-14'
            style={{ height: '21px' }}
            dangerouslySetInnerHTML={{ __html: previewDescription }}
          />
        )}
        {tags?.length > 0 && (
          <div className='specific-top-margin-10'>
            <InteractionTags tags={tags} />
          </div>
        )}
      </Link>
    </li>
  );
};

const InteractionCardSkeleton = () => (
  <li className='my-2'>
    <div className='d-block px-4 py-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <Skeleton height={14} width={120} />
        <div className='d-flex align-items-center'>
          <Skeleton height={24} width={80} borderRadius={50} className='mr-3' />
          <Skeleton height={14} width={100} />
        </div>
      </div>
      <Skeleton height={16} width={260} className='mb-1' />
      <Skeleton height={14} width={180} />
    </div>
  </li>
);

export const InteractionListSkeleton = () => (
  <SkeletonTheme baseColor='#f2f2f2'>
    <ul className='policy-list px-0 items-list'>
      {Array(6)
        .fill()
        .map((_, i) => (
          <InteractionCardSkeleton key={i} />
        ))}
    </ul>
  </SkeletonTheme>
);

export const InteractionScrollSkeleton = () => (
  <SkeletonTheme baseColor='#f2f2f2'>
    <InteractionCardSkeleton />
  </SkeletonTheme>
);

export default InteractionCard;
